import { assert, debug } from './console-log'
import { Environment, IConfiguration } from './helpers/environment'
import * as Session from './helpers/session'
import { isBoolean, isNumber, isObject, isString } from './helpers/type-checker'
import { newGuid } from './helpers/utils'
import {
  IBrowserData,
  ILogEntry,
  IMetadata,
  LogLevel,
  MessageData,
  MessageOptions,
} from './interfaces'

const sessionStartTime = Session.startTime()
const errorValue = -1
const errorMessage = 'Error'
const messageEventName = 'Message'

export function getMetaData(currentConfig?: IConfiguration): IMetadata {
  return {
    correlationId: newGuid(),
    currentTime: new Date().toISOString(),
    environment: currentConfig
      ? Environment[currentConfig.Environment]
      : Environment[Environment.None],
    sessionId:
      currentConfig && currentConfig.CustomSessionId
        ? currentConfig.CustomSessionId
        : Session.guid(),
    sessionStartTime,
  }
}

export function getPageLoadTime(): number {
  // tslint:disable:deprecation
  const performanceTiming =
    window && window.performance ? window.performance.timing : null

  return performanceTiming
    ? performanceTiming.domContentLoadedEventEnd -
        performanceTiming.navigationStart
    : -1
  // tslint:enable:deprecation
}

// returns elapsed time since host page load/session time
export function getTimeSincePageStart(): number {
  let elapsedTime = -1
  const windowPerf = window && window.performance
  if (windowPerf) {
    const pageStartTime =
      windowPerf.timeOrigin ||
      // tslint:disable-next-line: deprecation
      (windowPerf.timing && windowPerf.timing.navigationStart)
    elapsedTime = pageStartTime && Date.now() - pageStartTime
  }

  return elapsedTime
}

export function getWindowDimensions() {
  return {
    windowHeight: window ? window.innerHeight : undefined,
    windowWidth: window ? window.innerWidth : undefined,
  }
}

export function getBrowserData(): IBrowserData {
  return {
    // from browser navigator object
    browserLang: navigator ? navigator.language : errorMessage,
    browserOnline: navigator ? navigator.onLine : false,
    browserUserAgent: navigator ? navigator.userAgent : errorMessage,

    // from browser screen object
    colorDepth: screen ? screen.colorDepth : errorValue,
    pixelDepth: screen ? screen.pixelDepth : errorValue,
    screenHeight: screen ? screen.height : errorValue,
    screenWidth: screen ? screen.width : errorValue,
  }
}

export function getMessageData(
  logLevel: LogLevel,
  eventTagId: number,
  messageOptions: MessageOptions,
  messageArgs?: unknown[]
): MessageData {
  debug(logLevel !== LogLevel.None, 'Invalid logLevel')
  assert(eventTagId > 0, 'Invalid tagId')
  debug(messageOptions, 'Invalid message')
  debug(messageArgs, 'Invalid messageDetails')

  const messageDetails: string[] | undefined =
    messageArgs && messageArgs.length > 0
      ? messageArgs.map(messageArg => {
          return typeof messageArg === 'string'
            ? messageArg
            : serializeObject(messageArg)
        })
      : undefined

  const { eventFlags, message } =
    typeof messageOptions === 'string'
      ? { eventFlags: undefined, message: messageOptions }
      : messageOptions

  return {
    eventFlags,
    eventTagId,
    logLevel,
    message,
    messageDetails,
  }
}

export function getEventName(logEntry: ILogEntry): string | null {
  let eventName: string | null = null

  if (!logEntry) {
    debug(logEntry, 'Invalid logEntry')

    return eventName
  }

  if (logEntry.eventActionData) {
    eventName = logEntry.eventActionData.eventActionType
  } else if (logEntry.messageData) {
    eventName = messageEventName
  }

  return eventName
}

export const enumerateObjectMembers = <T extends object>(
  inputObject: T | undefined,
  callback: (key: string, value: string | boolean | number) => void
) => {
  if (!inputObject) {
    return
  }

  Object.keys(inputObject).forEach(key => {
    const inputValue = inputObject[key as keyof T]
    // tslint:disable-next-line:strict-type-predicates
    if (inputValue !== undefined && inputValue !== null) {
      if (isObject(inputValue)) {
        callback(key, JSON.stringify(inputValue))
      } else if (
        isString(inputValue) ||
        isBoolean(inputValue) ||
        isNumber(inputValue)
      ) {
        callback(key, inputValue)
      }
    }
  })
}

export function getFlattenedData(
  logEntry: Omit<ILogEntry, 'eventFlags'>,
  callback: (key: string, value: string | boolean | number) => void
): void {
  debug(callback, 'Invalid callback')
  debug(logEntry, 'Invalid logEntry')

  enumerateObjectMembers(logEntry.metadata, callback)
  enumerateObjectMembers(logEntry.messageData, callback)
  enumerateObjectMembers(logEntry.eventActionData, callback)
  enumerateObjectMembers(logEntry.customData, callback)
  enumerateObjectMembers(logEntry.browserData, callback)
}

export const getTraceTagHex = (tagId: number) => `0x${tagId.toString(16)}`

const loggableFields = ['name', 'number', 'message', 'description']

// serializes input object
export function serializeObject(inputObject: unknown) {
  const handleErrorObject = (_key: string, value: unknown) => {
    if (value instanceof Error) {
      const errorObj: Error = { name: '', message: '' }
      Object.getOwnPropertyNames(value).forEach(errorKey => {
        const key = errorKey as keyof Error
        if (loggableFields.indexOf(errorKey.toLowerCase()) !== -1) {
          errorObj[key] = value[key] as string
        }
      })

      return errorObj
    }

    return value
  }

  return JSON.stringify(inputObject, handleErrorObject)
}
